<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div
        v-if="successMessage"
        class="alert alert-success alert-dismissible fade show fixed-alert"
        role="alert"
    >
      {{ $t('yearSettings.successMessage') }}
      <button
          type="button"
          class="btn-close"
          @click="successMessage = ''"
      ></button>
    </div>
    <div
        v-if="errorMessage"
        class="alert alert-danger alert-dismissible fade show fixed-alert"
        role="alert"
    >
      {{ $t('yearSettings.errorMessage') }}
      <button
          type="button"
          class="btn-close"
          @click="errorMessage = ''"
      ></button>
    </div>

    <!-- Page Title -->
    <h2 class="mt-4">{{ $t('yearSettings.title') }}</h2>

    <!-- Add Year Setting Button -->
    <div class="mb-4">
      <button class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('yearSettings.addYear') }}
      </button>
    </div>

    <!-- Year Settings Table -->
    <table v-if="yearSettings.length > 0" class="table table-hover">
      <thead>
      <tr>
        <th>{{ $t('id') }}</th>
        <th>{{ $t('year') }}</th>
        <th>{{ $t('settings') }}</th>
        <th>{{ $t('holidays') }}</th>
        <th>{{ $t('status') }}</th>
        <th>{{ $t('actions') }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="yearSetting in yearSettings" :key="yearSetting.id">
        <td>{{ yearSetting.id }}</td>
        <td>{{ yearSetting.year }}</td>
        <td>
          {{ $t('yearSettings.enableTwoWeekRule') }}: {{ yearSetting.enable_two_week_rule ? $t('yes') : $t('no') }}
          <br/>
          {{ $t('yearSettings.vacationQuota') }}: {{ yearSetting.paid_vacation_quota }} {{ $t('days') }} <br/>
          {{ $t('yearSettings.sickLeaveQuota') }}: {{ yearSetting.paid_sick_leave_quota }} {{ $t('days') }} <br/>
          {{ $t('yearSettings.transferDeadline') }}: {{ formatDate(yearSetting.transfer_date) }}
        </td>
        <td>
          <ol>
            <li v-for="holiday in yearSetting.holidays" :key="holiday.id">
              {{ holiday.name }} ({{ formatDate(holiday.date) }})
            </li>
          </ol>
          <router-link
              :to="{ name: 'Holidays', query: { year_id: yearSetting.id } }"
              class="btn btn-sm btn-secondary"
          >
            <i class="bi bi-calendar"></i> {{ $t('yearSettings.manageHolidays', {year: yearSetting.year}) }}
          </router-link>
        </td>
        <td>
            <span :class="yearSetting.is_closed ? 'badge bg-danger' : 'badge bg-success'">
              {{ yearSetting.is_closed ? $t('yearSettings.closed') : $t('yearSettings.open') }}
            </span>
        </td>
        <td>
          <button
              class="btn btn-primary btn-sm"
              @click="openModal('edit', yearSetting)"
          >
            <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
          </button>
          <button
              class="btn btn-warning btn-sm"
              @click="toggleYearStatus(yearSetting)"
              :disabled="isProcessing"
          >
            <i class="bi bi-toggle-on"></i>
            {{ yearSetting.is_closed ? $t('yearSettings.reopen') : $t('yearSettings.close') }}
          </button>
          <button
              class="btn btn-danger btn-sm"
              @click="openConfirmDelete(yearSetting.id)"
          >
            <i class="bi bi-trash"></i> {{ $t('delete') }}
          </button>
        </td>
      </tr>
      </tbody>
    </table>
    <div v-if="yearSettings.length === 0" class="alert alert-warning">
      {{ $t('yearSettings.noYearSettings') }}
    </div>

    <!-- Modal for Add/Edit Year Setting -->
    <div class="modal fade" id="yearSettingModal" tabindex="-1" aria-labelledby="yearSettingModalLabel"
         aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="yearSettingModalLabel">{{
                isEditMode ? $t('yearSettings.editYearSetting') : $t('yearSettings.addYearSetting')
              }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitYearSetting">
              <div class="mb-3">
                <label for="yearInput" class="form-label">{{ $t('year') }}</label>
                <input v-model="yearSettingForm.year" type="number" class="form-control" id="yearInput" required/>
              </div>
              <div class="mb-3">
                <label for="vacationQuotaInput" class="form-label">{{ $t('yearSettings.vacationQuota') }}</label>
                <input v-model="yearSettingForm.paid_vacation_quota" type="number" class="form-control"
                       id="vacationQuotaInput" required/>
              </div>
              <div class="mb-3">
                <label for="sickLeaveQuotaInput" class="form-label">{{ $t('yearSettings.sickLeaveQuota') }}</label>
                <input v-model="yearSettingForm.paid_sick_leave_quota" type="number" class="form-control"
                       id="sickLeaveQuotaInput" required/>
              </div>
              <div class="mb-3 form-check">
                <input v-model="yearSettingForm.enable_two_week_rule" type="checkbox" class="form-check-input"
                       id="twoWeekRuleCheckbox"/>
                <label class="form-check-label" for="twoWeekRuleCheckbox">{{
                    $t('yearSettings.enableTwoWeekRule')
                  }}</label>
              </div>
              <div class="mb-3">
                <label for="transferDateInput" class="form-label">{{ $t('yearSettings.transferDeadline') }}</label>
                <input v-model="yearSettingForm.transfer_date" type="date" class="form-control" id="transferDateInput"
                       required/>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitYearSetting">{{
                isEditMode ? $t('update') : $t('add')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">Confirm Delete</h3>
        <p>Are you sure you want to delete this year?</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteYearSetting">Yes, delete</button>
          <button class="btn btn-primary" @click="cancelDelete">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "YearSettingList",
  data() {
    return {
      yearSettings: [],
      isProcessing: false,
      successMessage: "",
      errorMessage: "",
      isEditMode: false,
      yearSettingForm: {
        id: null,
        year: "",
        paid_vacation_quota: 20,
        paid_sick_leave_quota: 10,
        enable_two_week_rule: false,
        transfer_date: null,
        is_closed: false,
      },
      modalInstance: null,
      showConfirm: false,
      yearSettingId: null,
    };
  },
  mounted() {
    this.getYearSettings();
    this.$nextTick(() => {
      const modalElement = document.getElementById("yearSettingModal");
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  methods: {
    async getYearSettings() {
      try {
        const response = await axios.get("/api/v1/vacation/year-settings/");
        this.yearSettings = response.data.results || response.data;
      } catch (error) {
        this.errorMessage = "Failed to fetch year settings.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },
    openModal(mode, yearSetting = null) {
      if (mode === "add") {
        this.isEditMode = false;
        this.yearSettingForm = {
          id: null,
          year: "",
          paid_vacation_quota: 20,
          paid_sick_leave_quota: 10,
          enable_two_week_rule: false,
          transfer_date: this.getNextTransferDate(),
          is_closed: false,
        };
      } else if (mode === "edit" && yearSetting) {
        this.isEditMode = true;
        this.yearSettingForm = {...yearSetting};
      }
      this.modalInstance.show();
    },
    closeModal() {
      this.modalInstance.hide();
    },
    async submitYearSetting() {
      try {
        if (this.isEditMode) {
          if (!this.yearSettingForm.id) {
            this.errorMessage = "Error: Year ID is missing for editing.";
            return;
          }
          await axios.put(`/api/v1/vacation/year-settings/${this.yearSettingForm.id}/`, this.yearSettingForm);
          this.successMessage = "Year setting updated successfully.";
        } else {
          await axios.post("/api/v1/vacation/year-settings/", this.yearSettingForm);
          this.successMessage = "Year setting added successfully.";
        }
        this.getYearSettings();
      } catch (error) {
        this.errorMessage = "Failed to save year setting.";
      } finally {
        this.closeModal();
        setTimeout(() => {
          this.successMessage = "";
          this.errorMessage = "";
        }, 3000);
      }
    },
    async toggleYearStatus(yearSetting) {
      this.isProcessing = true;
      try {
        const updatedSetting = {...yearSetting, is_closed: !yearSetting.is_closed};
        await axios.put(`/api/v1/vacation/year-settings/${yearSetting.id}/`, updatedSetting);
        this.successMessage = `Year ${yearSetting.year} has been ${yearSetting.is_closed ? "reopened" : "closed"}.`;
        this.getYearSettings();
      } catch (error) {
        this.errorMessage = "Failed to toggle year status.";
      } finally {
        this.isProcessing = false;
        setTimeout(() => (this.successMessage = ""), 3000);
      }
    },
    async deleteYearSetting(yearSettingId) {
      try {
        await axios.delete(`/api/v1/vacation/year-settings/${this.yearSettingId}/`);
        this.successMessage = "Year setting deleted successfully.";
        this.getYearSettings();
      } catch (error) {
        this.errorMessage = "Failed to delete year setting.";
      } finally {
        this.showConfirm = false;
        setTimeout(() => {
          this.successMessage = "";
          this.errorMessage = "";
        }, 3000);
      }
    },
    openConfirmDelete(yearSettingId) {
      this.yearSettingId = yearSettingId;
      this.showConfirm = true;
    },
    cancelDelete() {
      this.showConfirm = false;
    },
    getNextTransferDate() {
      const now = new Date();
      const nextYear = now.getFullYear() + 1;
      return `${nextYear}-02-01`;
    }
    ,
    formatDate(date) {
      return date
          ? new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
          : "";
    }
    ,
  },
};
</script>
